import React from 'react';
import { Box, Text } from 'core/components';
import DeviceConfigView from 'app/components/device/DeviceConfigView';
import styled from 'styled-components';
import { FiTerminal } from 'react-icons/fi';
import ToolResultCard from '../components/ToolResultCard';

const CommandSection = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  margin: 0 8px 4px 8px;
  background: ${({ theme }) => (theme.name === 'dark' ? '#1e1e1e' : '#263238')};
  border-radius: 4px;
  font-family: ${({ theme }) => theme.fonts.monospace};

  .command-icon {
    color: ${({ theme }) => theme.colors.muted};
    flex-shrink: 0;
  }

  .command-text {
    color: ${({ theme }) => (theme.name === 'dark' ? '#e3e3e3' : '#cad3f5')};
    font-size: 14px;
    font-weight: 500;
    word-break: break-all;
  }
`;

const CodeMirrorContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  background: ${({ theme }) => (theme.name === 'dark' ? '#1e1e1e' : '#263238')};
  border-radius: 4px;
  overflow: hidden;
  margin: 0 8px 8px 8px;

  > div {
    flex: 1;
    display: flex;
  }

  .cm-editor {
    flex: 1;
    height: 100%;
    width: 100%;
    background-color: transparent;
  }

  .cm-scroller {
    flex: 1;
    overflow: auto;
  }

  .cm-gutters {
    background-color: transparent;
    color: ${({ theme }) => (theme.name === 'dark' ? '#6e6e6e' : '#537f7e')};
    border: none;
  }
`;

const DeviceSection = styled(Box)`
  margin: 0 8px 12px 8px;

  &:last-child {
    margin-bottom: 8px;
  }
`;

const headerColors = {
  light: { success: '#1e3a2f', error: '#3a1e1e', successText: '#81c784', errorText: '#e57373', name: '#e0e0e0' },
  dark: { success: '#1c2f25', error: '#371225', successText: '#a0deb5', errorText: '#f5b6d6', name: '#e3e3e3' }
};

const DeviceHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${({ theme, success }) => {
    const c = theme.name === 'dark' ? headerColors.dark : headerColors.light;
    return success ? c.success : c.error;
  }};
  border-radius: 4px 4px 0 0;
  border-left: 3px solid ${({ success }) => (success ? '#4caf50' : '#f44336')};

  .device-name {
    color: ${({ theme }) => (theme.name === 'dark' ? headerColors.dark.name : headerColors.light.name)};
    font-size: 14px;
    font-weight: 500;
  }

  .status-indicator {
    margin-left: auto;
    font-size: 12px;
    color: ${({ theme, success }) => {
      const c = theme.name === 'dark' ? headerColors.dark : headerColors.light;
      return success ? c.successText : c.errorText;
    }};
  }
`;

const DeviceOutput = styled.div`
  background: ${({ theme }) => (theme.name === 'dark' ? '#1e1e1e' : '#263238')};
  border-radius: 0 0 4px 4px;
  overflow: hidden;
  max-height: 400px;
  display: flex;
  flex-direction: column;

  > div {
    flex: 1;
    min-height: 0;
    display: flex;
  }

  .cm-editor {
    flex: 1;
    width: 100%;
    background-color: transparent;
  }

  .cm-scroller {
    flex: 1;
    overflow: auto !important;
  }

  .cm-gutters {
    background-color: transparent;
    color: ${({ theme }) => (theme.name === 'dark' ? '#6e6e6e' : '#537f7e')};
    border: none;
  }
`;

const ErrorMessage = styled(Box)`
  padding: 12px;
  background: ${({ theme }) => (theme.name === 'dark' ? '#1e1e1e' : '#263238')};
  border-radius: 0 0 4px 4px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#f5b6d6' : '#e57373')};
  font-family: ${({ theme }) => theme.fonts.monospace};
  font-size: 13px;
`;

const ScrollableContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

/**
 * DeviceCommandToolResult
 * Shows the result of a device command execution.
 * Supports both single-device (legacy) and multi-device formats.
 */
const DeviceCommandToolResult = ({ tool, onCloseToolResult, isInlineMode = false }) => {
  const result = tool?.raw_result ?? {};

  // Check if this is the new multi-device format
  const isMultiDevice = result.results && Array.isArray(result.results);
  const devices = isMultiDevice ? result.results : null;

  // Legacy single-device format
  const hasLegacyOutput = !isMultiDevice && result.output !== undefined;

  return (
    <ToolResultCard
      tool={tool}
      onCloseToolResult={onCloseToolResult}
      className="tool-result-device-command"
      title={tool.title || 'Device Command Result'}
      isInlineMode={isInlineMode}
      minHeight={450}
    >
      {result.command && (
        <CommandSection>
          <FiTerminal className="command-icon" size={16} />
          <Text className="command-text">{result.command}</Text>
        </CommandSection>
      )}

      {/* Multi-device format with scrollable container */}
      {isMultiDevice && (
        <ScrollableContainer>
          {devices.map((device) => {
            const deviceIdentifier = device.deviceName
              ? `${device.deviceName} (ID: ${device.deviceId})`
              : `ID ${device.deviceId}`;

            return (
              <DeviceSection key={device.deviceId}>
                <DeviceHeader success={device.success}>
                  <Text className="device-name">{deviceIdentifier}</Text>
                  <Text className="status-indicator">{device.success ? '✅ Success' : '❌ Failed'}</Text>
                </DeviceHeader>
                {device.error ? (
                  <ErrorMessage>{device.error}</ErrorMessage>
                ) : (
                  <DeviceOutput>
                    <DeviceConfigView configText={device.output || ''} syntax="plain" />
                  </DeviceOutput>
                )}
              </DeviceSection>
            );
          })}
        </ScrollableContainer>
      )}

      {/* Legacy single-device format */}
      {hasLegacyOutput && (
        <CodeMirrorContainer>
          <DeviceConfigView configText={result.output || ''} syntax="plain" />
        </CodeMirrorContainer>
      )}
    </ToolResultCard>
  );
};

export default DeviceCommandToolResult;
